import { textVariants } from "@/components/ui/Text";
import { cva } from "class-variance-authority";
import * as React from "react";
import cn from "twsx";
import styles from "./List.module.css";

// Linear data table: a framed panel with a 32px header row (12/500 tertiary,
// sentence case) and 13px body rows separated by hairlines. Call sites place
// tables and columns (width, alignment, wrapping, visibility); everything that
// changes how a cell looks is a variant here.

type TableProps = React.ComponentProps<"table"> & {
  ref?: React.Ref<HTMLTableElement>;
  /**
   * `false` renders the bare table for surfaces that own their scrolling and
   * frame, such as an edge-to-edge page list or a markdown block, so sticky
   * headers and columns follow that scroll container.
   */
  framed?: boolean;
  /** Caps a framed table's height (360/460/540px) so its body scrolls under a sticky header. */
  scroll?: "md" | "lg" | "xl";
};

const tableScrollHeights = { md: styles.scrollMd, lg: styles.scrollLg, xl: styles.scrollXl } as const;

const Table = ({ className, ref, framed = true, scroll, ...props }: TableProps) => {
  const table = (
    <table
      ref={ref}
      className={cn(textVariants({ tone: "inherit" }), "w-full caption-bottom border-collapse bg-bg-surface", className)}
      {...props}
    />
  );
  if (!framed) return table;
  return (
    <div className="w-full overflow-hidden rounded-control border border-border-primary bg-bg-surface">
      <div className={cn("w-full overflow-x-auto", scroll && "overflow-y-auto", scroll && tableScrollHeights[scroll])}>{table}</div>
    </div>
  );
};
Table.displayName = "Table";

type TableHeaderProps = React.ComponentProps<"thead"> & {
  ref?: React.Ref<HTMLTableSectionElement>;
  /** Keeps the header row visible while the table body scrolls. */
  sticky?: boolean;
};

const TableHeader = ({ className, ref, sticky = false, ...props }: TableHeaderProps) => (
  <thead
    ref={ref}
    className={cn("bg-bg-surface [&_tr]:border-b [&_tr]:border-border-primary [&_tr]:hover:bg-transparent", sticky && "sticky top-0 z-20", className)}
    {...props}
  />
);
TableHeader.displayName = "TableHeader";

type TableBodyProps = React.ComponentProps<"tbody"> & {
  ref?: React.Ref<HTMLTableSectionElement>;
  /** `alternate` shades a body, for tables that split rows into one body per group and alternate them. */
  tone?: "default" | "alternate";
  /** `cards` stacks each row as a card below the 2xl breakpoint; pair it with `layout="card"` rows. */
  layout?: "table" | "cards";
};

const TableBody = ({ className, ref, tone = "default", layout = "table", ...props }: TableBodyProps) => (
  <tbody
    ref={ref}
    className={cn(
      "[&_tr:last-child]:border-0",
      tone === "alternate" && "bg-bg-primary/50",
      layout === "cards" && ["block bg-bg-primary p-2 [&_tr:last-child]:border", "2xl:table-row-group 2xl:bg-transparent 2xl:p-0 2xl:[&_tr:last-child]:border-0"],
      className,
    )}
    {...props}
  />
);
TableBody.displayName = "TableBody";

type TableFooterProps = React.ComponentProps<"tfoot"> & {
  ref?: React.Ref<HTMLTableSectionElement>;
  /** Keeps the totals row visible while the table body scrolls. */
  sticky?: boolean;
};

const TableFooter = ({ className, ref, sticky = false, ...props }: TableFooterProps) => (
  <tfoot
    ref={ref}
    className={cn("border-t border-border-primary bg-bg-primary/50 font-medium text-text-primary [&_tr]:hover:bg-transparent", sticky && "sticky bottom-0 z-20 bg-bg-primary", className)}
    {...props}
  />
);
TableFooter.displayName = "TableFooter";

const tableRowVariants = cva("group/row border-b border-border-primary transition-colors data-[state=selected]:bg-fill-row-selected", {
  variants: {
    tone: {
      default: "hover:bg-fill-hover",
      group: "bg-bg-primary/40 hover:bg-fill-hover/60",
      nested: "bg-bg-primary/20",
      summary: "border-border-secondary bg-bg-primary",
    },
    layout: {
      row: "",
      card: [
        "mb-2 grid gap-x-4 gap-y-1 rounded-control border border-border-primary bg-bg-surface p-2 last:mb-0",
        "2xl:mb-0 2xl:table-row 2xl:rounded-none 2xl:border-0 2xl:border-b 2xl:p-0",
      ],
    },
  },
  defaultVariants: { tone: "default", layout: "row" },
});

type TableRowProps = React.ComponentProps<"tr"> & {
  ref?: React.Ref<HTMLTableRowElement>;
  /** `group` marks a parent row with nested detail, `nested` is a row inside that detail, `summary` closes a group with its totals. */
  tone?: "default" | "group" | "nested" | "summary";
  /** `card` lays the row out as a grid card below 2xl; columns come from the call site's grid classes. */
  layout?: "row" | "card";
};

const TableRow = ({ className, ref, tone, layout, ...props }: TableRowProps) => (
  <tr
    ref={ref}
    className={cn(tableRowVariants({ tone, layout }), className)}
    {...props}
  />
);
TableRow.displayName = "TableRow";

const stickyCellVariants = {
  start: "sticky left-0 z-10 border-r border-border-primary bg-bg-surface group-hover/row:bg-fill-hover",
  end: "sticky right-0 z-10 border-l border-border-primary bg-bg-surface group-hover/row:bg-fill-hover",
};

// Unframed tables run edge to edge on a page, so their outer columns line up
// with the page gutter instead of the cell padding.
const insetCellVariants = { start: styles.insetStart, end: styles.insetEnd };

type TableHeadProps = React.ComponentProps<"th"> & {
  ref?: React.Ref<HTMLTableCellElement>;
  /** Pins the column to the start or end edge while the table scrolls sideways. */
  sticky?: "start" | "end";
  /** Aligns the outer column of an unframed table with the page gutter. */
  inset?: "start" | "end";
  /** Right-aligns a number column. */
  numeric?: boolean;
};

const numericCellClassName = "whitespace-nowrap text-right tabular-nums";

const TableHead = ({ className, ref, sticky, inset, numeric, ...props }: TableHeadProps) => (
  <th
    ref={ref}
    className={cn(
      "h-8 px-3 text-left align-middle text-xs font-medium text-text-tertiary [&:has([role=checkbox])]:pr-0",
      props.scope === "row" && "h-auto bg-bg-primary/50 py-2 align-top text-text-secondary",
      sticky && stickyCellVariants[sticky],
      inset && insetCellVariants[inset],
      numeric && numericCellClassName,
      className,
    )}
    {...props}
  />
);
TableHead.displayName = "TableHead";

const tableCellVariants = cva("px-3 py-2 align-middle [&:has([role=checkbox])]:pr-0", {
  variants: {
    emphasis: {
      default: "",
      strong: "font-medium text-text-primary",
      muted: "text-text-secondary",
      total: "text-sm font-semibold text-text-primary",
    },
    mono: {
      true: "font-mono text-xs",
      false: "",
    },
    flush: {
      true: "p-0",
      false: "",
    },
    indent: {
      0: "",
      1: "pl-8",
      2: "pl-14",
      3: "pl-20",
    },
  },
  defaultVariants: { emphasis: "default", mono: false, flush: false, indent: 0 },
});

type TableCellProps = React.ComponentProps<"td"> & {
  ref?: React.Ref<HTMLTableCellElement>;
  emphasis?: "default" | "strong" | "muted" | "total";
  mono?: boolean;
  /** Removes padding for a cell that holds a nested table. */
  flush?: boolean;
  sticky?: "start" | "end";
  inset?: "start" | "end";
  /** Right-aligns a number column. */
  numeric?: boolean;
  /** Indents a tree column by nesting level. */
  indent?: 0 | 1 | 2 | 3;
};

const TableCell = ({ className, ref, emphasis, mono, flush, sticky, inset, numeric, indent, ...props }: TableCellProps) => (
  <td
    ref={ref}
    className={cn(tableCellVariants({ emphasis, mono, flush, indent }), sticky && stickyCellVariants[sticky], inset && insetCellVariants[inset], numeric && numericCellClassName, className)}
    {...props}
  />
);
TableCell.displayName = "TableCell";

type TableCaptionProps = React.ComponentProps<"caption"> & {
  ref?: React.Ref<HTMLTableCaptionElement>;
  /** Keeps the caption for screen readers only. */
  srOnly?: boolean;
};

const TableCaption = ({ className, ref, srOnly = false, ...props }: TableCaptionProps) => (
  <caption
    ref={ref}
    className={cn(srOnly ? "sr-only" : "px-3 py-2 text-left text-xs text-text-tertiary", className)}
    {...props}
  />
);
TableCaption.displayName = "TableCaption";

const TableEmpty = ({
  children = "No rows available.",
  colSpan = 1,
}: {
  children?: React.ReactNode;
  colSpan?: number;
}) => (
  <tr>
    <td colSpan={colSpan} className="px-3 py-10 text-center text-text-tertiary">
      {children}
    </td>
  </tr>
);
TableEmpty.displayName = "TableEmpty";

export { Table, TableBody, TableCaption, TableCell, TableEmpty, TableFooter, TableHead, TableHeader, TableRow };
